"use client";
import React, { useState, useEffect } from "react";
import { User, Lock, Save, Eye, EyeOff, Settings, Shield, Sparkles } from "lucide-react";
import { useProfile } from "../context/ProfileContext";
import { CDN_URL } from "../config/config";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState("profile");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { profile, fetchProfile, updateProfile, updatePassword, loading } = useProfile();

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      setProfileForm({
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        role: profile.role || "Admin",
        avatar: null, // only for new upload
      });
    }
  }, [profile]);

  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    avatar: null, // only for new upload
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handleProfileSave = async () => {
    if (!profileForm.name || !profileForm.phone) {
      alert("Name and phone are required");
      return;
    }

    const success = await updateProfile({
      name: profileForm.name,
      phone: profileForm.phone,
      imageFile: profileForm.avatar,
    });

    if (success) {
      alert("Profile updated successfully");
    }
  };

  const handlePasswordReset = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("New passwords don't match!");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      alert("Password must be at least 6 characters long!");
      return;
    }

    const result = await updatePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });

    if (result.success) {
      alert(result.message);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } else {
      alert(result.message);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileForm({ ...profileForm, avatar: file });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-red-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="red">
                    <Settings className="h-4 w-4 mr-1" />
                    Account Settings
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="blue">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Profile Management
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Settings
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Manage your account settings and preferences. Update your profile information and security settings.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Status</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {profile?.role || "Loading..."}
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-green-600">Active</div>
                  <div className="text-sm font-semibold text-green-900 mt-1">
                    Profile
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex space-x-1 bg-gray-50 p-1 rounded-2xl ring-1 ring-gray-200">
            <button
              onClick={() => setActiveTab("profile")}
              className={cx(
                "flex items-center gap-2 px-6 py-3 rounded-xl font-extrabold text-sm transition-all duration-200",
                activeTab === "profile"
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-red-600 hover:bg-white"
              )}
            >
              <User size={18} />
              Profile
            </button>
            <button
              onClick={() => setActiveTab("password")}
              className={cx(
                "flex items-center gap-2 px-6 py-3 rounded-xl font-extrabold text-sm transition-all duration-200",
                activeTab === "password"
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-red-600 hover:bg-white"
              )}
            >
              <Lock size={18} />
              Security
            </button>
          </div>
        </div>

        <SectionDivider
          label={activeTab === "profile" ? "Profile Information" : "Security Settings"}
          right={
            <div className="flex items-center gap-2">
              <Pill tone={activeTab === "profile" ? "blue" : "red"} size="sm">
                {activeTab === "profile" ? "Personal Data" : "Password Management"}
              </Pill>
            </div>
          }
        />

        {/* PROFILE TAB */}
        {activeTab === "profile" && (
          <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
            <div className="p-7 sm:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Avatar Section */}
                <div className="lg:col-span-1">
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto mb-6 bg-red-50 rounded-3xl flex items-center justify-center overflow-hidden ring-1 ring-red-100">
                      {profileForm.avatar ? (
                        <img
                          src={URL.createObjectURL(profileForm.avatar)}
                          className="w-full h-full object-cover"
                          alt="Avatar preview"
                        />
                      ) : profile?.image ? (
                        <img
                          src={`${CDN_URL}/${profile.image}`}
                          className="w-full h-full object-cover"
                          alt="Profile avatar"
                        />
                      ) : (
                        <User size={48} className="text-red-600" />
                      )}
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      id="avatar-upload"
                    />
                    <label
                      htmlFor="avatar-upload"
                      className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-2xl cursor-pointer hover:bg-red-700 transition-all duration-200 font-extrabold text-sm ring-1 ring-red-600 shadow-sm"
                    >
                      <User size={16} />
                      Change Avatar
                    </label>

                    <div className="mt-4 p-4 bg-gray-50 rounded-2xl ring-1 ring-gray-200">
                      <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600 mb-2">
                        Profile Status
                      </div>
                      <Pill tone="green" size="sm">
                        <Shield className="h-3 w-3 mr-1" />
                        Verified Account
                      </Pill>
                    </div>
                  </div>
                </div>

                {/* Form Section */}
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-extrabold text-gray-700 mb-3">
                        Email Address
                      </label>
                      <input
                        type="text"
                        value={profileForm.email}
                        readOnly
                        className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 bg-gray-50 text-gray-500 font-medium"
                      />
                      <p className="mt-2 text-xs text-gray-500">Email cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-sm font-extrabold text-gray-700 mb-3">
                        Role
                      </label>
                      <input
                        type="text"
                        value={profileForm.role}
                        readOnly
                        className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 bg-gray-50 text-gray-500 font-medium"
                      />
                      <p className="mt-2 text-xs text-gray-500">Role is system assigned</p>
                    </div>

                    <div>
                      <label className="block text-sm font-extrabold text-gray-700 mb-3">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 font-medium transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-extrabold text-gray-700 mb-3">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers
                          if (/^\d*$/.test(value)) {
                            setProfileForm({ ...profileForm, phone: value });
                          }
                        }}
                        onBlur={() => {
                          // Optional: check length on blur
                          if (profileForm.phone.length !== 10) {
                            alert("Phone number must be 10 digits");
                          }
                        }}
                        className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 font-medium transition-all duration-200"
                        placeholder="Enter your phone number"
                      />
                    </div>

                  </div>

                  <div className="mt-8 flex items-center gap-4">
                    <button
                      onClick={handleProfileSave}
                      disabled={loading}
                      className={cx(
                        "flex items-center gap-2 px-6 py-3 rounded-2xl font-extrabold text-sm transition-all duration-200 ring-1 shadow-sm",
                        loading
                          ? "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                          : "bg-red-600 text-white ring-red-600 hover:bg-red-700"
                      )}
                    >
                      {loading ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                      ) : (
                        <Save size={18} />
                      )}
                      {loading ? "Saving..." : "Save Changes"}
                    </button>

                    <div className="text-xs text-gray-500">
                      Changes will be saved to your profile immediately
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASSWORD TAB */}
        {activeTab === "password" && (
          <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
            <div className="p-7 sm:p-10">
              <div className="max-w-2xl">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-extrabold text-gray-700 mb-3">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        className="w-full px-4 py-3 pr-12 rounded-2xl ring-1 ring-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 font-medium transition-all duration-200"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-extrabold text-gray-700 mb-3">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        className="w-full px-4 py-3 pr-12 rounded-2xl ring-1 ring-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 font-medium transition-all duration-200"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-extrabold text-gray-700 mb-3">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className="w-full px-4 py-3 pr-12 rounded-2xl ring-1 ring-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 font-medium transition-all duration-200"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="pt-4 flex items-center gap-4">
                    <button
                      onClick={handlePasswordReset}
                      disabled={loading}
                      className={cx(
                        "flex items-center gap-2 px-6 py-3 rounded-2xl font-extrabold text-sm transition-all duration-200 ring-1 shadow-sm",
                        loading
                          ? "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                          : "bg-red-600 text-white ring-red-600 hover:bg-red-700"
                      )}
                    >
                      {loading ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                      ) : (
                        <Lock size={18} />
                      )}
                      {loading ? "Updating..." : "Update Password"}
                    </button>

                    <div className="text-xs text-gray-500">
                      Password will be updated immediately
                    </div>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="mt-8 p-6 bg-red-50 rounded-3xl ring-1 ring-red-100">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-extrabold text-red-900 mb-3">Password Requirements</h4>
                      <ul className="text-sm text-red-700 space-y-2">
                        <li className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-red-400"></div>
                          At least 6 characters long
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-red-400"></div>
                          Include uppercase and lowercase letters
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-red-400"></div>
                          Include at least one number
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-red-400"></div>
                          Include at least one special character
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
